// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import ArrowRight from "@keller/XDL/Icons/ArrowRight";
import { buttonTransparent } from "@keller/XDL/PreStyle/button";
import color from "@keller/XDL/Utilities/color";
import * as React from "react";
import { css } from "styled-components";

export const linkArrowStyle: LinkPresentationProps = {
    icon: {
        iconProps: {
            src: ArrowRight,
            size: 18,
            color: "primary",
            css: css`
                opacity: 0.33;
                margin-left: 8px;
            `,
        },
        position: "right",
    },
    typographyProps: {
        size: 14,
        ellipsis: true,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 16px;
        `,
    },
    css: css`
        justify-content: left;
        // prevent take full width
        align-self: flex-start;

        &:hover {
            & > ${IconWrapper}, & > ${TypographyStyle} {
                color: ${color("common.ui1")};
            }
            & > ${IconWrapper} {
                opacity: 0.8;
            }
        }
    `,
};

interface IconButtonProps {
    typographyProps?: TypographyProps;
}

export const iconButtonCommonStyles = (
    iconSrc: React.ComponentType | string,
    iconSize?: number,
    buttonProps?: IconButtonProps,
) => {
    return {
        mergeCss: true,
        icon: {
            src: iconSrc,
            position: "left",
        },
        typographyProps: {
            color: "secondary",
            size: 13,
            weight: "bold",
            transform: "capitalize",
            ...buttonProps?.typographyProps,
            css: css`
                ${buttonProps?.typographyProps?.css}
                letter-spacing: 0;
                line-height: 14px;
                &:hover {
                    color: ${color("text.link")};
                }
            `,
        },
        css: css`
            padding: 0;
            ${buttonTransparent}
            ${IconWrapper} {
                margin-right: 5px;
                color: ${color("secondary")};
                &,
                & svg {
                    width: ${iconSize || 14}px;
                    height: ${iconSize || 14}px;
                }
            }

            &:hover:not([disabled]),
            &:focus:not([disabled]),
            &:focus:hover {
                &,
                & ${IconWrapper} {
                    color: ${color("text.link")};
                }
            }

            &:disabled {
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("secondary", 0.5)};
                    background-color: transparent;
                }
            }

            &,
            * {
                font-size: ${`${buttonProps?.typographyProps?.size}px` || "13px"};
                font-weight: ${buttonProps?.typographyProps?.weight || "600"};
            }
        `,
    } as ButtonPresentationProps;
};

export const iconLinkCommonStyles = (iconSrc: React.ComponentType | string) => {
    return {
        color: "primary",
        icon: {
            iconProps: {
                src: iconSrc,
                size: 13,
                color: "primary",
                css: css`
                    margin-right: 4px;
                    opacity: 0.33;
                `,
            },
            position: "left",
        },
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 12px;
            `,
        },
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            &:disabled {
                &,
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("primary", 0.5)};
                }
            }
            &:hover {
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("common.ui1")};
                }
                & ${IconWrapper} {
                    opacity: 1;
                }
            }
        `,
    } as LinkPresentationProps;
};
